import DeleteIcon from '@mui/icons-material/Delete';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkIcon from '@mui/icons-material/Link';
import XIcon from '@mui/icons-material/X';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { BroadcastAccountItem } from 'providers/contexts/extensions/Broadcast';
import { memo, useCallback, useMemo, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import type { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import {
  checkBroadcastChangeSettingsPermission,
  checkBroadcastFullAccessPermission,
  checkBroadcastSendNotificationsPermission,
  checkSiteAdminPermission,
  PermissionInfo,
} from 'utility/permissions';
import { SimpleConfirmDeleteModal } from 'components/Modals/SimpleConfirmDeleteModal';
import { useSearchItems } from 'components/Search/components/SearchItemsProvider';

// ---------------------------------------------------------
// Permission Check
// ---------------------------------------------------------

/**
 * Check if the user has permissions to manage (disconnect) accounts.
 * Requires a higher permission than just viewing — read-only users
 * can see the list but cannot disconnect accounts.
 *
 * Allowed: Site Admin, Full Access, Change Settings, or Send Notifications
 * Not allowed: Read Notifications only
 */
export function checkConnectionManagePerms(
  sessionConfig?: Parameters<typeof checkSiteAdminPermission>[0],
): PermissionInfo {
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: hasFullAccess } = checkBroadcastFullAccessPermission(sessionConfig);
  const { granted: hasChangeSettings } =
    checkBroadcastChangeSettingsPermission(sessionConfig);
  const { granted: hasSendNotifications, name: sendPermName } =
    checkBroadcastSendNotificationsPermission(sessionConfig);

  if (userIsAdmin || hasFullAccess || hasChangeSettings || hasSendNotifications) {
    return {
      info: undefined,
      disabled: false,
    };
  }

  return {
    info: sendPermName,
    disabled: true,
  };
}

// ---------------------------------------------------------
// Provider Icon Helper
// ---------------------------------------------------------

export const getProviderIcon = (provider?: string) => {
  switch (provider?.toLowerCase()) {
    case 'facebook':
      return <FacebookIcon color="primary" />;
    case 'twitter':
    case 'x':
      return <XIcon />;
    default:
      return <LinkIcon />;
  }
};

// ---------------------------------------------------------
// Platform Cell Component
// ---------------------------------------------------------

export type ConnectionPlatformCellProps = {
  account?: BroadcastAccountItem | null;
};

export const ConnectionPlatformCell = memo(({ account }: ConnectionPlatformCellProps) => {
  const { t } = useTranslation();
  if (!account) return null;

  const providerLabel = account.provider
    ? account.provider.charAt(0).toUpperCase() + account.provider.slice(1)
    : t('blox.notifier.settings.social_accounts.unknown_provider');

  return (
    <Stack
      direction="row"
      spacing={1}
      data-testid={`connection-platform-${account.id ?? 'unknown'}`}
      sx={{
        alignItems: 'center',
        width: '100%',
      }}
    >
      {getProviderIcon(account.provider)}
      <Typography variant="body2">{providerLabel}</Typography>
    </Stack>
  );
});
ConnectionPlatformCell.displayName = 'ConnectionPlatformCell';

// ---------------------------------------------------------
// Actions Cell Component (Disconnect)
// ---------------------------------------------------------

export type ConnectionActionsCellProps = {
  account?: BroadcastAccountItem | null;
};

export const ConnectionActionsCell = memo(({ account }: ConnectionActionsCellProps) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const { sessionConfig } = useCosmos();
  const { refetch } = useSearchItems();

  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);

  const managePerms: PermissionInfo = useMemo(() => {
    const perms = checkConnectionManagePerms(sessionConfig);
    return {
      ...perms,
      info: perms.info
        ? t('blox.permissions.msg.required_short', { name: perms.info })
        : undefined,
    };
  }, [sessionConfig, t]);

  const handleDisconnect = useCallback(async () => {
    if (!account?.id) return;

    try {
      await dataProvider.deleteBroadcastAccountId({
        path: { id: account.id },
      });
      notify(t('blox.notifier.settings.social_accounts.account_disconnected'), {
        type: 'success',
      });
      setDisconnectDialogOpen(false);
      // Refresh the list
      refetch();
    } catch {
      notify(t('blox.notifier.settings.social_accounts.error_disconnect'), {
        type: 'error',
      });
    }
  }, [account, dataProvider, notify, t, refetch]);

  if (!account?.id) return null;

  const providerLabel = account.provider
    ? account.provider.charAt(0).toUpperCase() + account.provider.slice(1)
    : t('blox.notifier.settings.social_accounts.unknown_provider');

  return (
    <Box data-testid={`connection-actions-${account.id}`}>
      <Tooltip
        title={
          managePerms.disabled
            ? managePerms.info || ''
            : t('blox.notifier.settings.social_accounts.disconnect')
        }
      >
        <span>
          <IconButton
            color="error"
            onClick={() => setDisconnectDialogOpen(true)}
            disabled={managePerms.disabled}
            data-testid={`disconnect-account-${account.id}`}
          >
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>

      <SimpleConfirmDeleteModal
        open={disconnectDialogOpen}
        onCancel={() => setDisconnectDialogOpen(false)}
        onConfirm={handleDisconnect}
        headerTxt={t('blox.notifier.settings.social_accounts.disconnect')}
        titleTxt={t('blox.notifier.settings.social_accounts.disconnect_confirm')}
        subTxt={`${account.user_name ?? '—'} (${providerLabel})`}
        confirmButtonText={t('blox.notifier.settings.social_accounts.disconnect')}
        cancelButtonText={t('blox.actions.cancel')}
        initialHeight={200}
      />
    </Box>
  );
});
ConnectionActionsCell.displayName = 'ConnectionActionsCell';

// ---------------------------------------------------------
// Pre-Search Content Component (top bar area)
// ---------------------------------------------------------

export const ConnectionsPreSearchContent = memo(() => {
  return null;
});
ConnectionsPreSearchContent.displayName = 'ConnectionsPreSearchContent';
